import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from './firebase';
import { useDomain } from './DomainContext';
import { getAlertSummary, ALERTS_CHANGED } from './apiEmails';
import { SITE_SECTIONS, activeSiteSection, siteBase, newPagePath } from './siteNav';

const ALERT_POLL_MS = 2 * 60 * 1000;

const IS_MAC = typeof navigator !== 'undefined'
  && /Mac|iPhone|iPad|iPod/i.test(navigator.platform || navigator.userAgent || '');
const NEW_PAGE_SHORTCUT = IS_MAC ? '⌘N' : 'Ctrl+N';

// Global tabs: prefix-matched against the current pathname.
const GLOBAL_TABS = [
  { to: '/', label: 'Home', prefixes: ['/'], title: 'What to do next' },
  { to: '/domains', label: 'Sites', prefixes: ['/domains'], title: 'Every site, with traffic and earnings' },
  { to: '/search', label: 'All pages', prefixes: ['/search'], title: 'Search pages across every site' },
  { to: '/rankings', label: 'Rankings', prefixes: ['/rankings'], title: 'Google search performance for every site' },
  { to: '/images', label: 'Media', prefixes: ['/images'], title: 'Images and files available to every site' },
];
const MORE_TABS = [
  { to: '/emails', label: 'Inbox', prefixes: ['/emails'] },
  { to: '/bookmarks', label: 'Bookmarks', prefixes: ['/bookmarks'] },
];
const SETTINGS_PREFIXES = ['/settings', '/users', '/api-keys'];

const cx = (...parts) => parts.filter(Boolean).join(' ');

const Header = ({ authenticated }) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [menuOpen, setMenuOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [userPhoto, setUserPhoto] = useState(null);
  const [userName, setUserName] = useState('');
  const [alerts, setAlerts] = useState({ feedback: 0, contact: 0 });
  const { domains, selectedDomain, selectDomain, loadDomains, domainsLoaded } = useDomain();
  const navigate = useNavigate();
  const location = useLocation();
  const menuRef = useRef(null);

  // Publish the sticky header's height so page-level sticky bars can sit below it.
  useEffect(() => {
    const el = menuRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const apply = () => document.documentElement.style.setProperty('--app-header-h', `${el.offsetHeight}px`);
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => ro.disconnect();
    // The header is not rendered until auth resolves, so re-measure when it flips.
  }, [authenticated, isMobile]);
  const moreRef = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (!mobile) setMenuOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close menus on route change
  useEffect(() => {
    setMenuOpen(false);
    setMoreOpen(false);
  }, [location.pathname]);

  // Close menus on outside click / Escape
  useEffect(() => {
    if (!menuOpen && !moreOpen) return;
    const handleClick = (e) => {
      if (menuOpen && menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
      if (moreOpen && moreRef.current && !moreRef.current.contains(e.target)) setMoreOpen(false);
    };
    const handleKey = (e) => {
      if (e.key === 'Escape') { setMenuOpen(false); setMoreOpen(false); }
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [menuOpen, moreOpen]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setUserPhoto(user.photoURL);
        setUserName(user.displayName || user.email || '');
      } else {
        setUserPhoto(null);
        setUserName('');
      }
    });
    return () => unsubscribe();
  }, []);

  // Reader feedback and contact form messages need their own alert: the
  // inbox they land in is mostly spam.
  useEffect(() => {
    if (!authenticated) return undefined;
    let cancelled = false;
    const refresh = () => getAlertSummary()
      .then((d) => {
        if (!cancelled) {
          setAlerts({ feedback: d.by_type?.page_feedback || 0, contact: d.by_type?.contact_form || 0 });
        }
      })
      .catch(() => {});
    refresh();
    const t = setInterval(refresh, ALERT_POLL_MS);
    window.addEventListener(ALERTS_CHANGED, refresh);
    return () => { cancelled = true; clearInterval(t); window.removeEventListener(ALERTS_CHANGED, refresh); };
  }, [authenticated]);

  useEffect(() => {
    if (authenticated && !domainsLoaded) {
      loadDomains();
    }
  }, [authenticated, domainsLoaded, loadDomains]);

  // Ctrl+N / Cmd+N keyboard shortcut → new page for selected domain
  useEffect(() => {
    if (!authenticated || !selectedDomain) return;
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'n') {
        const tag = document.activeElement?.tagName?.toLowerCase();
        if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
        e.preventDefault();
        navigate(`/domain/${selectedDomain.id}/pages/new/edit`);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [authenticated, selectedDomain, navigate]);

  const handleDomainChange = (e) => {
    const domainId = e.target.value;
    if (!domainId) {
      selectDomain(null);
      if (location.pathname.startsWith('/domain/')) navigate('/domains');
      return;
    }
    const domain = domains.find(d => d.id === domainId);
    if (domain) {
      selectDomain(domain);
      navigate(`/domain/${domain.id}`);
    }
  };

  const { pathname } = location;
  const isPrefix = (prefixes) => prefixes.some(p => pathname === p || pathname.startsWith(p + '/'));
  const settingsActive = isPrefix(SETTINGS_PREFIXES);
  const moreActive = MORE_TABS.some(t => isPrefix(t.prefixes));

  const domainPaths = useMemo(() => {
    if (!selectedDomain) return null;
    return { overview: siteBase(selectedDomain.id), newPage: newPagePath(selectedDomain.id) };
  }, [selectedDomain]);

  /* The site bar: Overview · Pages · Growth · Settings (siteNav.js groups the
     routes; Growth and Settings pages carry their own sub-tabs). */
  const siteActive = activeSiteSection(pathname, selectedDomain?.id);
  const siteLinks = (mobile) => (selectedDomain ? SITE_SECTIONS.map((s) => {
    const active = siteActive.section === s.key;
    return (
      <Link
        key={s.key}
        to={`${domainPaths.overview}${s.path}`}
        title={s.hint}
        className={cx('nav-link', mobile && 'mobile', active && 'active')}
        style={mobile ? undefined : { fontSize: '12px', padding: '4px 10px' }}
        aria-current={active ? 'page' : undefined}
      >
        {s.label}
      </Link>
    );
  }) : null);

  const sortedDomains = [...domains].sort((a, b) => {
    const aFav = a.is_favorite || false;
    const bFav = b.is_favorite || false;
    if (aFav && !bFav) return -1;
    if (!aFav && bFav) return 1;
    return (a.hostname || '').localeCompare(b.hostname || '');
  });

  const domainLabel = selectedDomain ? (selectedDomain.hostname || selectedDomain.name) : '';

  // Render helpers (plain functions, not nested components, so the select keeps focus across renders)
  const renderDomainSelect = (style) => (
    <select
      aria-label="Select domain"
      value={selectedDomain?.id || ''}
      onChange={handleDomainChange}
      className={cx('nav-select', selectedDomain && 'has-value')}
      style={style}
    >
      <option value="">Select domain…</option>
      {sortedDomains.map(d => (
        <option key={d.id} value={d.id}>
          {d.is_favorite ? '★ ' : ''}{d.hostname || d.name}
        </option>
      ))}
    </select>
  );

  const renderAvatar = (size = 34) => (
    <Link
      to="/settings"
      title={userName ? `${userName} — Settings` : 'Settings'}
      aria-label="Settings"
      aria-current={settingsActive ? 'page' : undefined}
      className="nav-avatar-link"
    >
      {userPhoto ? (
        <img
          src={userPhoto}
          alt={userName}
          referrerPolicy="no-referrer"
          className="nav-avatar"
          style={{
            width: `${size}px`,
            height: `${size}px`,
            objectFit: 'cover',
            borderColor: settingsActive ? 'var(--color-primary)' : undefined,
          }}
        />
      ) : (
        <div
          className="nav-avatar"
          style={{
            width: `${size}px`,
            height: `${size}px`,
            backgroundColor: 'var(--color-primary)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: `${Math.round(size * 0.4)}px`,
            fontWeight: 'bold',
            borderColor: settingsActive ? 'var(--color-primary)' : undefined,
          }}
        >
          {userName ? userName[0].toUpperCase() : '⚙'}
        </div>
      )}
    </Link>
  );

  const alertCount = alerts.feedback + alerts.contact;
  const plural = (n, word) => `${n} ${word}${n !== 1 ? 's' : ''}`;
  const alertTitle = [
    alerts.feedback ? plural(alerts.feedback, 'reader feedback note') : null,
    alerts.contact ? plural(alerts.contact, 'contact form message') : null,
  ].filter(Boolean).join(' · ');

  // Opens whichever list has something to handle (feedback first when both do).
  const renderFeedbackBadge = () => (alertCount > 0 ? (
    <Link
      to={`/emails?filter=${alerts.feedback ? 'feedback' : 'contact'}`}
      className="nav-link"
      title={`${alertTitle} to handle`}
      aria-label={`${alertCount} unhandled messages: ${alertTitle}`}
      style={{ display: 'flex', alignItems: 'center', gap: '6px', marginRight: '6px' }}
    >
      <span aria-hidden="true">💬</span>
      <span style={{
        minWidth: '18px', height: '18px', padding: '0 5px', borderRadius: '9px', boxSizing: 'border-box',
        background: 'var(--color-danger, #dc2626)', color: '#fff', fontSize: '11px', fontWeight: 700,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {alertCount > 99 ? '99+' : alertCount}
      </span>
    </Link>
  ) : null);

  const sectionLabel = (text) => (
    <div style={{ padding: '4px 8px', fontSize: '11px', fontWeight: '700', color: 'var(--color-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
      {text}
    </div>
  );

  // ==================== MOBILE LAYOUT ====================
  if (isMobile) {
    if (!authenticated) return null;
    return (
      <div ref={menuRef} style={{ position: 'sticky', top: 0, zIndex: 1000 }}>
        <nav aria-label="Main" style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '0 12px',
          height: '48px',
          backgroundColor: 'var(--color-surface)',
          borderBottom: '1px solid var(--color-border)',
          boxShadow: 'var(--shadow-sm)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Link to="/" style={{ display: 'flex', alignItems: 'center' }}>
              <img src="/logo192.png" alt="Home" className="nav-logo" style={{ width: '32px', height: '32px', borderRadius: 'var(--radius)' }} />
            </Link>
            {selectedDomain && (
              <Link
                to={domainPaths.overview}
                style={{ fontSize: '13px', fontWeight: '600', color: '#374151', maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textDecoration: 'none' }}
              >
                {domainLabel}
              </Link>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {renderFeedbackBadge()}
            {renderAvatar(30)}
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label="Toggle menu"
              aria-expanded={menuOpen}
              style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                width: '36px',
                height: '36px',
                border: 'none',
                borderRadius: 'var(--radius)',
                backgroundColor: menuOpen ? '#f3f4f6' : 'transparent',
                cursor: 'pointer',
                gap: '4px',
                padding: '8px',
              }}
            >
              <span style={{
                display: 'block', width: '18px', height: '2px', backgroundColor: '#374151', borderRadius: '1px',
                transition: 'transform 0.2s, opacity 0.2s',
                transform: menuOpen ? 'translateY(6px) rotate(45deg)' : 'none',
              }} />
              <span style={{
                display: 'block', width: '18px', height: '2px', backgroundColor: '#374151', borderRadius: '1px',
                transition: 'opacity 0.2s',
                opacity: menuOpen ? 0 : 1,
              }} />
              <span style={{
                display: 'block', width: '18px', height: '2px', backgroundColor: '#374151', borderRadius: '1px',
                transition: 'transform 0.2s, opacity 0.2s',
                transform: menuOpen ? 'translateY(-6px) rotate(-45deg)' : 'none',
              }} />
            </button>
          </div>
        </nav>

        {menuOpen && (
          <div style={{
            backgroundColor: 'var(--color-surface)',
            borderBottom: '1px solid var(--color-border)',
            boxShadow: 'var(--shadow-md)',
            padding: '8px',
            display: 'flex',
            flexDirection: 'column',
            gap: '2px',
            animation: 'slideDown 0.15s ease-out',
          }}>
            {sectionLabel('Navigation')}
            {[...GLOBAL_TABS, ...MORE_TABS].map(tab => (
              <Link
                key={tab.to}
                to={tab.to}
                className={cx('nav-link', 'mobile', isPrefix(tab.prefixes) && 'active')}
                aria-current={isPrefix(tab.prefixes) ? 'page' : undefined}
              >
                {tab.label}
              </Link>
            ))}

            {domains.length > 0 && (
              <>
                <div style={{ height: '1px', backgroundColor: 'var(--color-border)', margin: '6px 0' }} />
                {sectionLabel('Domain')}
                <div style={{ padding: '4px 8px' }}>
                  {renderDomainSelect({ width: '100%', maxWidth: 'none', padding: '8px 12px', fontSize: '14px' })}
                </div>
              </>
            )}

            {selectedDomain ? (
              <>
                <div style={{ height: '1px', backgroundColor: 'var(--color-border)', margin: '6px 0' }} />
                {sectionLabel(domainLabel)}
                {siteLinks(true)}
                <div style={{ padding: '8px 8px 4px' }}>
                  <Link to={domainPaths.newPage} className="btn btn-primary btn-sm btn-block">+ New Page</Link>
                </div>
              </>
            ) : domains.length > 0 && (
              <div style={{ padding: '4px 8px 8px', fontSize: '12px', color: 'var(--color-muted)' }}>
                Select a domain to manage its pages
              </div>
            )}

            <div style={{ height: '1px', backgroundColor: 'var(--color-border)', margin: '6px 0' }} />
            <Link to="/settings" className={cx('nav-link', 'mobile', settingsActive && 'active')}>Console settings</Link>
          </div>
        )}
      </div>
    );
  }

  // ==================== DESKTOP LAYOUT ====================
  return (
    <div ref={menuRef} style={{ position: 'sticky', top: 0, zIndex: 1000 }}>
      {/* Global row */}
      <nav aria-label="Main" style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '0 8px',
        height: '48px',
        backgroundColor: 'var(--color-surface)',
        borderBottom: '1px solid var(--color-border)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <Link to="/" style={{ display: 'flex', alignItems: 'center', marginRight: '4px' }}>
            <img src="/logo192.png" alt="Home" className="nav-logo" style={{ width: '32px', height: '32px', padding: '2px', borderRadius: 'var(--radius)' }} />
          </Link>
          {authenticated && (
            <>
              {GLOBAL_TABS.map(tab => (
                <Link
                  key={tab.to}
                  to={tab.to}
                  className={cx('nav-link', isPrefix(tab.prefixes) && 'active')}
                  aria-current={isPrefix(tab.prefixes) ? 'page' : undefined}
                  title={tab.title}
                >
                  {tab.label}
                </Link>
              ))}
              <div ref={moreRef} style={{ position: 'relative' }}>
                <button
                  type="button"
                  className={cx('nav-link', moreActive && 'active')}
                  aria-haspopup="menu"
                  aria-expanded={moreOpen}
                  onClick={() => setMoreOpen(o => !o)}
                >
                  More ▾
                </button>
                {moreOpen && (
                  <div className="nav-menu" role="menu">
                    {MORE_TABS.map(tab => (
                      <Link
                        key={tab.to}
                        to={tab.to}
                        role="menuitem"
                        className={cx('nav-link', isPrefix(tab.prefixes) && 'active')}
                        onClick={() => setMoreOpen(false)}
                      >
                        {tab.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {authenticated && (
          <div style={{ display: 'flex', alignItems: 'center', marginLeft: '8px' }}>
            {renderFeedbackBadge()}
            {renderAvatar()}
          </div>
        )}
      </nav>

      {/* Domain context bar */}
      {authenticated && domainsLoaded && (
        <div aria-label="Domain" style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          padding: '4px 12px',
          minHeight: '36px',
          backgroundColor: 'var(--color-bg)',
          borderBottom: '1px solid var(--color-border)',
          boxShadow: 'var(--shadow-sm)',
          fontSize: '12px',
        }}>
          {renderDomainSelect()}
          {selectedDomain ? (
            <>
              {siteLinks(false)}
              <Link
                to={domainPaths.newPage}
                className="btn btn-primary btn-sm"
                style={{ marginLeft: '4px' }}
                title={`New page (${NEW_PAGE_SHORTCUT})`}
              >
                + New Page
              </Link>
              <span style={{ fontSize: '10px', color: 'var(--color-muted)', marginLeft: '2px', fontFamily: 'system-ui', userSelect: 'none' }} title="Keyboard shortcut">{NEW_PAGE_SHORTCUT}</span>
            </>
          ) : (
            <span style={{ color: 'var(--color-muted)', marginLeft: '4px' }}>
              {domains.length > 0 ? 'Select a domain to manage its pages' : 'No domains yet'}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default Header;
